import { useState, useEffect, useRef } from "react";
import { analytics } from "../firebase-config";
import { Helmet } from 'react-helmet-async';
import firebase from "firebase/app";
import "firebase/auth";
import api from "../libs/api";

// Type definition for backend API request
interface BackendChatRequest {
    message: string;
    history?: Array<{
        role: 'user' | 'assistant';
        content: string;
        timestamp?: string;
    }>;
    userId?: string;
}

export default function AIAssistant() {
    analytics.logEvent('page_view', {
        page_title: 'ai_assistant'
    });

    const [messages, setMessages] = useState([
        {
            id: 1,
            type: 'bot',
            content: 'Hello! I\'m your AI-powered food price analytics assistant. I can help you with price comparisons, market insights, and smart shopping recommendations across Nigerian markets. What would you like to know?',
            timestamp: new Date()
        }
    ]);
    const [inputMessage, setInputMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [hasUserAskedQuestions, setHasUserAskedQuestions] = useState(false);
    const [isLongRequest, setIsLongRequest] = useState(false);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const longRequestTimerRef = useRef<NodeJS.Timeout | null>(null);

    const sampleQuestions = [
        "What's the current price of rice in Lagos?",
        "Which market has the cheapest tomatoes this week?",
        "When is the best time to buy yam?",
        "Compare rice prices across different states",
        "What foods are trending up in price?",
        "Recommend budget-friendly protein sources"
    ];

    // Auto-scroll to bottom when new messages arrive
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages, isLoading]);

    // Focus input on mobile when not loading
    useEffect(() => {
        if (!isLoading && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isLoading]);

    // Cleanup timer on component unmount
    useEffect(() => {
        return () => {
            if (longRequestTimerRef.current) {
                clearTimeout(longRequestTimerRef.current);
            }
        };
    }, []);

    // Transform messages to backend format
    const formatHistoryForBackend = (messageHistory: typeof messages) => {
        return messageHistory
            .filter(msg => msg.id !== 1) // Exclude the initial welcome message
            .map(msg => ({
                role: msg.type === 'user' ? 'user' as const : 'assistant' as const,
                content: msg.content,
                timestamp: msg.timestamp.toISOString()
            }));
    };

    // Validate message content
    const validateMessage = (message: string): boolean => {
        if (!message || message.trim().length === 0) {
            return false;
        }
        if (message.length > 500) {
            console.warn('Message too long, truncating to 500 characters');
            return false;
        }
        return true;
    };

    const handleSendMessage = () => {
        if (!validateMessage(inputMessage) || isLoading) {
            return;
        }
        
        if (inputMessage.trim() && !isLoading) {
            // Add user message
            const userMessage = {
                id: messages.length + 1,
                type: 'user',
                content: inputMessage,
                timestamp: new Date()
            };

            // Update state immediately
            setMessages(prev => [...prev, userMessage]);
            setHasUserAskedQuestions(true);
            setIsLoading(true);
            setIsLongRequest(false);
            const currentInput = inputMessage;
            setInputMessage('');

            // Set timer for long request warning (after 8 seconds)
            longRequestTimerRef.current = setTimeout(() => {
                setIsLongRequest(true);
            }, 8000);

            // Get current user ID
            const currentUser = firebase.auth().currentUser;
            const userId = currentUser?.uid;
            
            // Log authentication status
            if (userId) {
                console.log('User authenticated:', userId);
            } else {
                console.log('User not authenticated, sending message without userId');
            }

            // Format conversation history for backend
            const formattedHistory = formatHistoryForBackend(messages);

            // Prepare request body
            const requestBody: BackendChatRequest = {
                message: currentInput,
                history: formattedHistory.length > 0 ? formattedHistory : undefined,
                userId: userId || undefined
            };

            // Debug log for development
            console.log('Sending to backend:', {
                message: requestBody.message,
                historyCount: requestBody.history?.length || 0,
                userId: requestBody.userId,
                formattedHistory: requestBody.history
            });

            // Use longer timeout for AI chat (30 seconds)
            api.post('/commodity/chat', requestBody, {
                timeout: 100000 // 30 seconds for AI responses
            }).then(response => {
                
                // Clear the long request timer on success
                if (longRequestTimerRef.current) {
                    clearTimeout(longRequestTimerRef.current);
                    longRequestTimerRef.current = null;
                }
                
                const aiResponse = {
                    id: messages.length + 2,
                    type: 'bot',
                    content: response.data.response,
                    timestamp: new Date()
                };
                setMessages(prev => [...prev, aiResponse]);
            }).catch(error => {
                console.error('Error sending message:', error);
                console.error('Request details:', requestBody);
                
                let errorMessage = 'Sorry, I\'m having trouble connecting right now. Please try again in a moment.';
                
                // Provide more specific error messages based on error type
                if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
                    // Timeout error
                    console.error('Request timeout:', error.message);
                    errorMessage = 'The AI is taking longer than usual to respond. This might be due to high server load or complex processing. Please try again or ask a simpler question.';
                } else if (error.response) {
                    // Server responded with error status
                    console.error('Server error:', error.response.data);
                    errorMessage = `Server error (${error.response.status}): ${error.response.data?.message || 'Please try again.'}`;
                } else if (error.request) {
                    // Request was made but no response received
                    console.error('Network error:', error.request);
                    errorMessage = 'Network connection error. Please check your internet connection and try again.';
                } else {
                    // Something else happened
                    console.error('Request setup error:', error.message);
                }
                
                const errorResponse = {
                    id: messages.length + 2,
                    type: 'bot',
                    content: errorMessage,
                    timestamp: new Date()
                };
                setMessages(prev => [...prev, errorResponse]);
            }).finally(() => {
                setIsLoading(false);
                setIsLongRequest(false);
                
                // Clear the long request timer
                if (longRequestTimerRef.current) {
                    clearTimeout(longRequestTimerRef.current);
                    longRequestTimerRef.current = null;
                }
            });
        }
    };

    const handleSampleQuestion = (question: string) => {
        if (!isLoading) {
            setInputMessage(question);
            setHasUserAskedQuestions(true);
            // Auto-focus input after setting sample question
            setTimeout(() => {
                inputRef.current?.focus();
            }, 100);
        }
    };

    return (
        <div className="h-screen bg-gradient-to-br from-emerald-50 via-cyan-50 to-blue-50 flex flex-col relative overflow-hidden">
            {/* Animated background elements */}
            <div className="absolute inset-0 overflow-hidden">
                {/* Floating gradient orbs */}
                <div className="absolute -top-4 -left-4 w-24 h-24 bg-gradient-to-r from-emerald-400 to-cyan-400 rounded-full opacity-20 animate-pulse"></div>
                <div className="absolute top-1/4 -right-8 w-32 h-32 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full opacity-15 animate-bounce" style={{animationDuration: '3s'}}></div>
                <div className="absolute bottom-1/4 -left-6 w-20 h-20 bg-gradient-to-r from-pink-400 to-rose-400 rounded-full opacity-20 animate-pulse" style={{animationDelay: '1s'}}></div>
                
                {/* Geometric patterns */}
                <div className="absolute inset-0 opacity-5">
                    <div className="absolute inset-0" style={{
                        backgroundImage: `radial-gradient(circle at 25px 25px, #10b981 2px, transparent 0)`,
                        backgroundSize: '50px 50px'
                    }}></div>
                </div>
                
                {/* Gradient mesh overlay */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent"></div>
            </div>
            <Helmet>
                <title>AI Assistant - ArketData Food Price Analytics</title>
                <meta
                    name="description"
                    content="Chat with our AI assistant for intelligent food price insights, market analysis, and smart shopping recommendations across Nigerian markets."
                />
            </Helmet>

            {/* Header */}
            <div className="relative z-10">
                {/* Gradient header with glass effect */}
                <div className="bg-gradient-to-r from-emerald-600 via-green-600 to-cyan-600 backdrop-blur-xl bg-opacity-90 shadow-xl border-b border-white/10">
                    <div className="px-4 py-4 sm:px-6 sm:py-5">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3 sm:space-x-4">
                                {/* Enhanced AI avatar 
                                <div className="relative">
                                    <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-white/30 to-white/10 backdrop-blur-lg rounded-2xl flex items-center justify-center shadow-2xl border border-white/20 transform hover:scale-105 transition-transform duration-200">
                                        <span className="text-white text-2xl sm:text-3xl animate-pulse">🤖</span>
                                    </div>
                                    <div className="absolute -top-1 -right-1 w-4 h-4 bg-gradient-to-r from-yellow-400 to-orange-400 rounded-full opacity-80 animate-bounce"></div>
                                </div>
                                */}
                                
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center space-x-2">
                                        <h1 className="text-xl sm:text-2xl font-bold text-white truncate bg-gradient-to-r from-white to-green-100 bg-clip-text text-transparent">
                                            Arket AI
                                        </h1>
                                        <div className="hidden sm:block w-2 h-2 bg-gradient-to-r from-green-300 to-emerald-300 rounded-full animate-pulse"></div>
                                    </div>
                                    <p className="text-sm sm:text-base text-green-50 font-medium opacity-90">
                                        Your Smart Food Price Assistant
                                    </p>
                                </div>
                            </div>
                            
                            {/* Status indicator */}
                            <div className="flex items-center space-x-2 sm:space-x-3">
                                <div className="flex items-center space-x-2 bg-white/10 backdrop-blur-sm rounded-full px-3 py-1.5 border border-white/20">
                                    <div className="relative">
                                        <span className="w-2.5 h-2.5 bg-green-400 rounded-full block animate-pulse"></span>
                                        <span className="absolute inset-0 w-2.5 h-2.5 bg-green-400 rounded-full animate-ping"></span>
                                    </div>
                                    <span className="text-xs sm:text-sm text-green-50 font-semibold">Online</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                
                {/* Decorative wave */}
                <div className="absolute bottom-0 left-0 right-0 h-4 bg-gradient-to-r from-emerald-600 via-green-600 to-cyan-600 transform -skew-y-1 origin-bottom-left"></div>
            </div>

            {/* Chat Messages */}
            <div className="flex-1 overflow-y-auto px-3 py-4 sm:px-6 sm:py-6 space-y-4 scrollbar-thin scrollbar-thumb-emerald-300 scrollbar-track-transparent relative z-10">
                {messages.length === 1 && (
                    <div className="text-center py-8 sm:py-12 animate-fade-in">
                        {/* Enhanced welcome section */}
                        <div className="relative mx-auto mb-6 w-24 h-24 sm:w-28 sm:h-28">
                            {/* Pulsing rings */}
                            <div className="absolute inset-0 bg-gradient-to-r from-emerald-400 to-cyan-400 rounded-full animate-ping opacity-20"></div>
                            <div className="absolute inset-2 bg-gradient-to-r from-green-400 to-blue-400 rounded-full animate-pulse opacity-30"></div>
                            
                            {/* Main avatar */}
                            <div className="relative w-24 h-24 sm:w-28 sm:h-28 bg-gradient-to-br from-emerald-100 via-green-50 to-cyan-100 rounded-full flex items-center justify-center shadow-2xl border-4 border-white">
                                <span className="text-4xl sm:text-5xl animate-bounce">🤖</span>
                            </div>
                            
                            {/* Floating particles */}
                            <div className="absolute -top-2 -right-2 w-4 h-4 bg-gradient-to-r from-yellow-400 to-orange-400 rounded-full animate-bounce" style={{animationDelay: '0.5s'}}></div>
                            <div className="absolute -bottom-1 -left-2 w-3 h-3 bg-gradient-to-r from-pink-400 to-purple-400 rounded-full animate-pulse" style={{animationDelay: '1s'}}></div>
                        </div>
                        
                        <div className="space-y-3 sm:space-y-4">
                            <h3 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-emerald-600 to-cyan-600 bg-clip-text text-transparent">
                                Welcome to ArketData AI! ✨
                            </h3>
                            <p className="text-sm sm:text-base text-gray-600 max-w-md mx-auto leading-relaxed">
                                I'm your intelligent assistant for food prices, market insights, and smart shopping recommendations across Nigeria. 
                            </p>
                            <div className="flex items-center justify-center space-x-2 text-xs sm:text-sm text-emerald-600 font-medium">
                                <span>💡</span>
                                <span>Ask me anything or try the suggestions below!</span>
                                <span>🚀</span>
                            </div>
                        </div>
                    </div>
                )}
                
                {messages.map((message, index) => (
                    <div
                        key={message.id}
                        className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'} animate-fade-in-up px-2 sm:px-0`}
                        style={{ animationDelay: `${index * 0.1}s` }}
                    >
                        <div className={`flex ${message.type === 'user' ? 'flex-row-reverse' : 'flex-row'} items-end space-x-2 sm:space-x-3 max-w-[90%] sm:max-w-md lg:max-w-lg`}>
                            {/* Enhanced AI Avatar */}
                            {message.type === 'bot' && (
                                <div className="relative flex-shrink-0 mb-2">
                                    <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-emerald-500 via-green-500 to-cyan-500 rounded-2xl flex items-center justify-center shadow-xl border-2 border-white/20 transform hover:scale-110 transition-transform duration-200">
                                        <span className="text-white text-sm sm:text-base animate-pulse">🤖</span>
                                    </div>
                                    {/* AI status indicator */}
                                    <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-gradient-to-r from-green-400 to-emerald-400 rounded-full border border-white shadow-sm"></div>
                                </div>
                            )}
                            
                            {/* Enhanced Message bubble */}
                            <div className="relative group">
                                <div
                                    className={`px-4 py-3 sm:px-5 sm:py-4 rounded-2xl shadow-xl backdrop-blur-sm transition-all duration-300 group-hover:shadow-2xl ${
                                        message.type === 'user'
                                            ? 'bg-gradient-to-br from-emerald-600 via-green-600 to-cyan-600 text-white rounded-br-md border border-white/20 animate-slide-in-right transform hover:scale-[1.02]'
                                            : 'bg-white/90 backdrop-blur-md text-gray-800 rounded-bl-md border border-gray-200/50 shadow-lg animate-slide-in-left transform hover:scale-[1.02]'
                                    }`}
                                >
                                    {/* Message content */}
                                    <p className={`text-sm sm:text-base leading-relaxed whitespace-pre-wrap ${
                                        message.type === 'user' ? 'text-white' : 'text-gray-800'
                                    }`}>
                                        {message.content}
                                    </p>
                                    
                                    {/* Timestamp with enhanced styling */}
                                    <div className={`flex items-center justify-between mt-2 text-xs ${
                                        message.type === 'user' ? 'text-emerald-100' : 'text-gray-500'
                                    }`}>
                                        <span>{message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                        {message.type === 'user' && (
                                            <span className="ml-2">✓</span>
                                        )}
                                    </div>
                                </div>
                                
                                {/* Decorative tail */}
                                <div className={`absolute top-4 w-3 h-3 transform rotate-45 ${
                                    message.type === 'user' 
                                        ? '-right-1 bg-gradient-to-br from-emerald-600 to-green-600' 
                                        : '-left-1 bg-white/90 border-l border-t border-gray-200/50'
                                }`}></div>
                            </div>
                            
                            {/* Enhanced User avatar */}
                            {message.type === 'user' && (
                                <div className="relative flex-shrink-0 mb-2 ml-2">
                                    <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-blue-500 via-indigo-500 to-purple-500 rounded-2xl flex items-center justify-center shadow-xl border-2 border-white/20 transform hover:scale-110 transition-transform duration-200">
                                        <span className="text-white text-sm sm:text-base">👤</span>
                                    </div>
                                    {/* User status indicator */}
                                    <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full border border-white shadow-sm"></div>
                                </div>
                            )}
                        </div>
                    </div>
                ))}
                
                {/* Enhanced Loading indicator */}
                {isLoading && (
                    <div className="flex justify-start animate-fade-in px-2 sm:px-0">
                        <div className="flex items-end space-x-2 sm:space-x-3 max-w-[90%] sm:max-w-md">
                            {/* Enhanced AI Avatar with loading animation */}
                            <div className="relative flex-shrink-0 mb-2">
                                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-emerald-500 via-green-500 to-cyan-500 rounded-2xl flex items-center justify-center shadow-xl border-2 border-white/20">
                                    <span className="text-white text-sm sm:text-base animate-pulse">🤖</span>
                                </div>
                                {/* Pulsing ring for loading */}
                                <div className="absolute inset-0 bg-gradient-to-r from-emerald-400 to-cyan-400 rounded-2xl animate-ping opacity-30"></div>
                                <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-gradient-to-r from-yellow-400 to-orange-400 rounded-full border border-white shadow-sm animate-bounce"></div>
                            </div>
                            
                            {/* Enhanced loading bubble */}
                            <div className="relative group">
                                <div className="px-4 py-3 sm:px-5 sm:py-4 rounded-2xl rounded-bl-md bg-white/95 backdrop-blur-md shadow-xl border border-gray-200/50 transform hover:scale-[1.02] transition-transform duration-300">
                                    <div className="flex items-center space-x-3 sm:space-x-4">
                                        {/* Enhanced bouncing dots */}
                                        <div className="flex space-x-1.5">
                                            <div className="w-2.5 h-2.5 bg-gradient-to-r from-emerald-500 to-green-500 rounded-full animate-bounce shadow-sm"></div>
                                            <div className="w-2.5 h-2.5 bg-gradient-to-r from-green-500 to-cyan-500 rounded-full animate-bounce shadow-sm" style={{animationDelay: '0.1s'}}></div>
                                            <div className="w-2.5 h-2.5 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full animate-bounce shadow-sm" style={{animationDelay: '0.2s'}}></div>
                                        </div>
                                        
                                        <div className="text-sm sm:text-base text-gray-700 font-medium">
                                            {isLongRequest ? (
                                                <div className="space-y-1">
                                                    <div className="flex items-center space-x-2">
                                                        <span>🧠</span>
                                                        <span>processing your request...</span>
                                                    </div>
                                                    <div className="text-xs text-gray-500 flex items-center space-x-1">
                                                        <span>⏱️</span>
                                                        <span>Analyzing market data - please wait</span>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="flex items-center space-x-2">
                                                    <span>💭</span>
                                                    <span>thinking...</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    
                                    {/* Progress indicator for long requests */}
                                    {isLongRequest && (
                                        <div className="mt-3 pt-2 border-t border-gray-100">
                                            <div className="w-full bg-gray-200 rounded-full h-1.5">
                                                <div className="bg-gradient-to-r from-emerald-500 to-cyan-500 h-1.5 rounded-full animate-pulse" style={{width: '60%'}}></div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                
                                {/* Decorative tail */}
                                <div className="absolute top-4 -left-1 w-3 h-3 transform rotate-45 bg-white/95 border-l border-t border-gray-200/50"></div>
                            </div>
                        </div>
                    </div>
                )}
                
                {/* Auto-scroll target */}
                <div ref={messagesEndRef} />
            </div>

            {/* Enhanced Sample Questions */}
            {!hasUserAskedQuestions && (
                <div className="px-4 py-6 sm:px-6 sm:py-8 bg-gradient-to-br from-white via-emerald-50/30 to-cyan-50/30 backdrop-blur-sm border-t border-gray-200/50 shadow-xl relative z-10">
                    {/* Header with enhanced styling */}
                    <div className="flex items-center justify-center mb-6">
                        <div className="flex items-center space-x-3 bg-gradient-to-r from-emerald-100 to-cyan-100 px-4 py-2 rounded-full shadow-lg border border-emerald-200">
                            <span className="text-xl animate-pulse">💡</span>
                            <p className="text-sm sm:text-base font-semibold bg-gradient-to-r from-emerald-600 to-cyan-600 bg-clip-text text-transparent">
                                Try asking me:
                            </p>
                            <span className="text-xl animate-bounce">✨</span>
                        </div>
                    </div>
                    
                    {/* Enhanced question grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                        {sampleQuestions.map((question, index) => (
                            <button
                                key={index}
                                onClick={() => handleSampleQuestion(question)}
                                className="group relative text-left p-4 bg-gradient-to-br from-white via-white to-gray-50/50 backdrop-blur-sm text-gray-700 rounded-2xl border border-gray-200/50 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-[1.02] hover:-translate-y-1 overflow-hidden"
                            >
                                {/* Gradient overlay on hover */}
                                <div className="absolute inset-0 bg-gradient-to-br from-emerald-50 to-cyan-50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl"></div>
                                
                                {/* Question content */}
                                <div className="relative z-10">
                                    <div className="flex items-start space-x-3">
                                        <span className="text-lg group-hover:animate-bounce">
                                            {index === 0 ? '🌾' : index === 1 ? '🏪' : index === 2 ? '⏰' : index === 3 ? '📊' : index === 4 ? '📈' : '💰'}
                                        </span>
                                        <span className="text-sm sm:text-base font-medium leading-relaxed group-hover:text-emerald-700 transition-colors duration-300">
                                            {question}
                                        </span>
                                    </div>
                                </div>
                                
                                {/* Decorative elements */}
                                <div className="absolute top-2 right-2 w-8 h-8 bg-gradient-to-br from-emerald-400/20 to-cyan-400/20 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                                <div className="absolute bottom-2 left-2 w-4 h-4 bg-gradient-to-br from-green-400/30 to-blue-400/30 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300 animate-pulse"></div>
                            </button>
                        ))}
                    </div>
                    
                    {/* Decorative bottom element */}
                    <div className="mt-6 text-center">
                        <div className="inline-flex items-center space-x-2 text-xs sm:text-sm text-emerald-600/70 font-medium">
                            <span>🚀</span>
                            <span>Click any question to get started</span>
                            <span>🎯</span>
                        </div>
                    </div>
                </div>
            )}

            {/* Enhanced Minimized Sample Questions */}
            {hasUserAskedQuestions && (
                <div className="px-4 py-3 sm:px-6 bg-gradient-to-r from-emerald-50/50 via-white to-cyan-50/50 backdrop-blur-sm border-t border-gray-200/50 shadow-lg relative z-10">
                    <div className="flex items-center space-x-3 overflow-x-auto scrollbar-none">
                        <div className="flex items-center space-x-2 flex-shrink-0">
                            <span className="text-base animate-pulse">💡</span>
                            <span className="text-xs font-medium text-emerald-600">Quick:</span>
                        </div>
                        
                        {sampleQuestions.slice(0, 3).map((question, index) => (
                            <button
                                key={index}
                                onClick={() => handleSampleQuestion(question)}
                                className="flex-shrink-0 group relative text-xs px-4 py-2 bg-gradient-to-r from-white to-gray-50 text-gray-700 rounded-full hover:from-emerald-50 hover:to-cyan-50 hover:text-emerald-700 transition-all duration-300 border border-gray-200/50 hover:border-emerald-300 shadow-md hover:shadow-lg transform hover:scale-105"
                            >
                                <span className="relative z-10">
                                    {question.length > 25 ? question.substring(0, 25) + '...' : question}
                                </span>
                                {/* Hover gradient overlay */}
                                <div className="absolute inset-0 bg-gradient-to-r from-emerald-100/50 to-cyan-100/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                            </button>
                        ))}
                        
                        <div className="flex items-center space-x-1 flex-shrink-0">
                            <span className="text-xs text-gray-400 px-2 py-1 bg-gray-100/50 rounded-full border border-gray-200">
                                +{sampleQuestions.length - 3} more
                            </span>
                        </div>
                    </div>
                </div>
            )}

            {/* Enhanced Input Area */}
            <div className="px-4 py-6 sm:px-6 sm:py-8 bg-gradient-to-br from-white via-emerald-50/20 to-cyan-50/20 backdrop-blur-lg border-t border-gray-200/50 shadow-2xl relative z-10">
                {/* Main input container */}
                <div className="flex items-end space-x-3 sm:space-x-4">
                    <div className="flex-1 relative group">
                        {/* Input field with enhanced styling */}
                        <div className="relative">
                            <input
                                ref={inputRef}
                                type="text"
                                value={inputMessage}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    if (value.length <= 500) {
                                        setInputMessage(value);
                                    }
                                }}
                                onKeyPress={(e) => e.key === 'Enter' && !isLoading && handleSendMessage()}
                                placeholder={
                                    isLoading 
                                        ? (isLongRequest ? "🧠 AI is processing complex request..." : "💭 AI is responding...") 
                                        : "💬 Ask about food prices, markets, trends..."
                                }
                                disabled={isLoading}
                                maxLength={500}
                                className={`w-full px-5 py-4 sm:py-5 border-2 rounded-3xl text-sm sm:text-base transition-all duration-300 backdrop-blur-md shadow-lg ${
                                    isLoading 
                                        ? 'bg-gray-100/80 border-gray-300 text-gray-500 cursor-not-allowed' 
                                        : 'bg-white/90 border-gray-300 text-gray-800 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100/50 focus:outline-none hover:border-emerald-400 hover:shadow-xl'
                                } placeholder:text-gray-500`}
                                style={{
                                    background: isLoading ? 'rgba(243, 244, 246, 0.8)' : 'rgba(255, 255, 255, 0.9)'
                                }}
                            />
                            
                            {/* Input focus indicator */}
                            {!isLoading && (
                                <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-emerald-400/10 to-cyan-400/10 opacity-0 group-focus-within:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
                            )}
                        </div>
                        
                        {/* Character count indicator with enhanced styling */}
                        {inputMessage.length > 400 && (
                            <div className={`absolute right-4 bottom-2 text-xs px-3 py-1.5 rounded-full font-medium shadow-lg backdrop-blur-sm ${
                                inputMessage.length > 450 
                                    ? 'bg-red-100/90 text-red-600 border border-red-200' 
                                    : 'bg-amber-100/90 text-amber-600 border border-amber-200'
                            }`}>
                                {inputMessage.length}/500
                            </div>
                        )}
                    </div>
                    
                    {/* Enhanced send button */}
                    <button
                        onClick={handleSendMessage}
                        disabled={isLoading || !inputMessage.trim()}
                        className={`px-5 py-4 sm:px-7 sm:py-5 rounded-3xl font-semibold transition-all duration-300 transform shadow-xl ${
                            isLoading || !inputMessage.trim()
                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed shadow-md'
                                : 'bg-gradient-to-r from-emerald-600 via-green-600 to-cyan-600 text-white hover:from-emerald-700 hover:via-green-700 hover:to-cyan-700 active:scale-95 hover:shadow-2xl hover:-translate-y-0.5'
                        }`}
                    >
                        {isLoading ? (
                            <div className="flex items-center space-x-2">
                                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                <span className="hidden sm:inline font-medium">
                                    {isLongRequest ? "Processing..." : "Sending"}
                                </span>
                            </div>
                        ) : (
                            <div className="flex items-center space-x-2">
                                <span className="text-xl">Send</span>
                            </div>
                        )}
                    </button>
                </div>
            </div>


        </div>
    );
} 